import Component from '@glimmer/component';
import { action, set } from '@ember/object';
import { service } from '@ember/service';
import { cached, tracked } from '@glimmer/tracking';
import { task, timeout } from 'ember-concurrency';
import { modifier } from 'ember-modifier';
import { TrackedAsyncData } from 'ember-async-data';
import { mapBy, sortBy } from '../utils/array-helpers';
import OfferingForm from './offering-form';
import mouseHoverToggle from '../modifiers/mouse-hover-toggle';
import { fn, get, concat } from '@ember/helper';
import FaIcon from '@fortawesome/ember-fontawesome/components/fa-icon';
import t from 'ember-intl/helpers/t';
import { and, eq } from 'ember-truth-helpers';
import includes from '../helpers/includes';
import IliosTooltip from './ilios-tooltip';
import mapBy0 from '../helpers/map-by';
import join from '../helpers/join';
import reverse from '../helpers/reverse';
import FadeText from './fade-text';
import TruncateText from './truncate-text';
import OfferingUrlDisplay from './offering-url-display';
import UserStatus from './user-status';
import UserNameInfo from './user-name-info';
import { on } from '@ember/modifier';
import set0 from 'ember-set-helper/helpers/set';
import {
  faPenToSquare,
  faTrash,
  faUniversalAccess,
  faUserPlus,
  faUsers,
} from '@fortawesome/free-solid-svg-icons';
import scrollIntoView from '../modifiers/scroll-into-view';

export default class OfferingManagerComponent extends Component {
  @service intl;
  @tracked isEditing = false;
  @tracked showRemoveConfirmation = false;
  @tracked hoveredGroups = [];

  setLearnerGroupElement = modifier((element, [id]) => {
    set(this, `learnerGroupElement${id}`, element);
  });
  scrollOpts = {
    behavior: 'smooth',
  };

  @action
  toggleIsEditing() {
    this.isEditing = !this.isEditing;
    if (this.args.toggleIsEditing) {
      this.args.toggleIsEditing();
    }
  }

  @cached
  get learnerGroupsData() {
    return new TrackedAsyncData(this.args.offering.learnerGroups);
  }

  get learnerGroups() {
    return this.learnerGroupsData.isResolved ? this.learnerGroupsData.value : null;
  }

  get sortedLearnerGroups() {
    if (!this.learnerGroups) {
      return [];
    }
    return this.learnerGroups.toSorted((learnerGroupA, learnerGroupB) => {
      if ('title:desc' === this.sortBy) {
        return learnerGroupB.title.localeCompare(learnerGroupA.title, this.intl.primaryLocale, {
          numeric: true,
        });
      }
      return learnerGroupA.title.localeCompare(learnerGroupB.title, this.intl.primaryLocale, {
        numeric: true,
      });
    });
  }

  @cached
  get individualLearnersData() {
    return new TrackedAsyncData(this.args.offering.learners);
  }

  get individualLearners() {
    return this.individualLearnersData.isResolved ? this.individualLearnersData.value : [];
  }

  get sortedIndividualLearners() {
    if (!this.individualLearners.length) {
      return '';
    }

    return mapBy(sortBy(this.individualLearners, 'fullName'), 'fullName').join(', ');
  }

  @cached
  get sessionData() {
    return new TrackedAsyncData(this.args.offering?.session);
  }

  get session() {
    return this.sessionData.isResolved ? this.sessionData.value : null;
  }

  @cached
  get courseData() {
    return new TrackedAsyncData(this.session?.course);
  }

  get course() {
    return this.courseData.isResolved ? this.courseData.value : null;
  }

  @cached
  get cohortsData() {
    return new TrackedAsyncData(this.course?.cohorts);
  }

  get cohorts() {
    return this.cohortsData.isResolved ? this.cohortsData.value : null;
  }

  get cohortsLoaded() {
    return !!this.cohorts;
  }

  @action
  save(startDate, endDate, room, url, learnerGroups, learners, instructorGroups, instructors) {
    this.args.offering.setProperties({
      startDate,
      endDate,
      room,
      url,
      learnerGroups,
      learners,
      instructorGroups,
      instructors,
    });
    this.toggleIsEditing();
    return this.args.offering.save();
  }

  @action
  toggleHover(id) {
    if (this.hoveredGroups.includes(id)) {
      this.hoveredGroups = this.hoveredGroups.filter((theId) => theId !== id);
    } else {
      this.hoveredGroups = [...this.hoveredGroups, id];
    }
  }

  textCopied = task(async () => {
    await timeout(3000);
  });
  <template>
    <div
      class="offering-manager{{if this.showRemoveConfirmation ' show-remove-confirmation'}}"
      data-test-offering-manager
      ...attributes
    >
      {{#if this.cohortsLoaded}}
        {{#if this.isEditing}}
          <OfferingForm
            @showRoom={{true}}
            @showInstructors={{true}}
            @cohorts={{this.cohorts}}
            @courseStartDate={{this.course.startDate}}
            @courseEndDate={{this.course.endDate}}
            @close={{this.toggleIsEditing}}
            @save={{this.save}}
            @smallGroupMode={{false}}
            @offering={{@offering}}
            @session={{this.session}}
            @scrollToBottom={{false}}
          />
        {{else}}
          <div class="offering-manager-learners-and-learner-groups">
            {{#if this.individualLearners.length}}
              <div class="offering-manager-learners">
                <FadeText @text={{this.sortedIndividualLearners}} />
              </div>
            {{/if}}
            <div class="offering-manager-learner-groups">
              <ul>
                {{#each this.sortedLearnerGroups as |learnerGroup|}}
                  <li
                    {{this.setLearnerGroupElement learnerGroup.id}}
                    {{mouseHoverToggle (fn this.toggleHover learnerGroup.id)}}
                  >
                    {{learnerGroup.title}}
                    {{#if learnerGroup.needsAccommodation}}
                      <FaIcon
                        @icon={{faUniversalAccess}}
                        @title={{t "general.accommodationIsRequiredForLearnersInThisGroup"}}
                      />
                    {{/if}}
                    {{#unless learnerGroup.isTopLevelGroup}}
                      {{#if
                        (and
                          (get this (concat "learnerGroupElement" learnerGroup.id))
                          (includes learnerGroup.id this.hoveredGroups)
                        )
                      }}
                        <IliosTooltip
                          @target={{get this (concat "learnerGroupElement" learnerGroup.id)}}
                        >
                          <strong>
                            {{if
                              (eq learnerGroup.allParents.length 1)
                              (t "general.parentGroup")
                              (t "general.parentGroups")
                            }}:
                          </strong>
                          {{join " » " (reverse (mapBy0 "title" learnerGroup.allParents))}}
                        </IliosTooltip>
                      {{/if}}
                    {{/unless}}
                  </li>
                {{else}}
                  <li>
                    <FaIcon @icon={{faUsers}} />
                  </li>
                {{/each}}
              </ul>
            </div>
          </div>
          <div class="offering-manager-location">
            <TruncateText @text={{@offering.room}} @length={{10}} data-test-location />
            <OfferingUrlDisplay @url={{@offering.url}} data-test-url />
          </div>
          <div class="offering-manager-instructors">
            <ul>
              {{#each @offering.allInstructors as |user|}}
                <li data-test-instructor>
                  <UserStatus @user={{user}} />
                  <UserNameInfo @user={{user}} />
                </li>
              {{else}}
                <li>
                  <FaIcon @icon={{faUserPlus}} />
                </li>
              {{/each}}
            </ul>
          </div>
          {{#if @editable}}
            <div class="offering-manager-actions">
              <button
                class="link-button edit"
                type="button"
                title={{if
                  this.showRemoveConfirmation
                  (t "general.disabledByConfirmation")
                  (t "general.edit")
                }}
                disabled={{this.showRemoveConfirmation}}
                {{on "click" this.toggleIsEditing}}
                data-test-edit
              >
                <FaIcon
                  @icon={{faPenToSquare}}
                  class={{if this.showRemoveConfirmation "disabled" "enabled"}}
                />
              </button>
              {{#if @editable}}
                <button
                  class="link-button remove delete-button"
                  type="button"
                  title={{if
                    this.showRemoveConfirmation
                    (t "general.disabledByConfirmation")
                    (t "general.remove")
                  }}
                  disabled={{this.showRemoveConfirmation}}
                  {{on "click" (set0 this "showRemoveConfirmation" true)}}
                  data-test-remove
                >
                  <FaIcon
                    @icon={{faTrash}}
                    class={{if this.showRemoveConfirmation "disabled" "remove enabled"}}
                  />
                </button>
              {{else}}
                <FaIcon
                  @icon={{faTrash}}
                  class="disabled"
                  @title={{t "general.canNotDeleteSessionOffering"}}
                />
              {{/if}}
            </div>
          {{/if}}
          {{#if this.showRemoveConfirmation}}
            <div class="confirm-removal" {{scrollIntoView opts=this.scrollOpts}}>
              <div class="confirm-message">
                {{t
                  "general.confirmRemoveSessionOffering"
                  learnerGroupCount=@offering.learnerGroups.length
                }}
                <br />
                <div class="confirm-buttons">
                  <button type="button" class="remove text" {{on "click" (fn @remove @offering)}}>
                    {{t "general.yes"}}
                  </button>
                  <button
                    type="button"
                    class="cancel text"
                    {{on "click" (set0 this "showRemoveConfirmation" false)}}
                  >
                    {{t "general.cancel"}}
                  </button>
                </div>
              </div>
            </div>
          {{/if}}
        {{/if}}
      {{/if}}
    </div>
  </template>
}
